import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function ProductRvPlastics() {
  const { t } = useLanguage();

  const materialTypes = [
    {
      icon: 'ri-recycle-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('單一廢棄塑料再生', 'Single Waste Plastic Recycling', '単一廃プラスチック再生'),
      items: [
        t('rABS 再生', 'rABS Recycling', 'rABS再生'),
        t('rPET 再生', 'rPET Recycling', 'rPET再生'),
        t('rPE 再生', 'rPE Recycling', 'rPE再生'),
        t('rPP 再生', 'rPP Recycling', 'rPP再生'),
      ],
    },
    {
      icon: 'ri-git-merge-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('複合廢棄塑料相容再生', 'Composite Waste Plastic Compatible Recycling', '複合廃プラスチック相容再生'),
      items: [
        t('rPET / rPE 複合再生', 'rPET / rPE Composite Recycling', 'rPET / rPE 複合再生'),
        t('rPET / rPP 複合再生', 'rPET / rPP Composite Recycling', 'rPET / rPP 複合再生'),
        t('混合廢棄塑料相容處理', 'Mixed Waste Plastic Compatible Processing', '混合廃プラスチック相容処理'),
      ],
    },
    {
      icon: 'ri-building-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('終端應用', 'End Applications', '最終用途'),
      items: [
        t('民生用品', 'Consumer Goods', '民生用品'),
        t('工業零件', 'Industrial Parts', '工業部品'),
        t('包裝材料', 'Packaging Materials', '包装材料'),
        t('品牌低碳產品線', 'Brand Low-Carbon Product Lines', 'ブランド低炭素製品ライン'),
      ],
    },
  ];

  const coreValues = [
    t('解決混合廢棄塑膠相容性問題', 'Solve mixed waste plastic compatibility issues', '混合廃プラスチックの相容性問題を解決'),
    t('讓難以處理的廢料重回產品生命週期', 'Return difficult-to-process waste to the product lifecycle', '処理困難な廃材を製品ライフサイクルに戻す'),
    t('支援民生與工業多元應用場景', 'Support diverse consumer and industrial application scenarios', '民生・工業の多様な応用シーンをサポート'),
    t('協助品牌端建立高比例再生料產品', 'Help brands establish high recycled content products', 'ブランドが高再生材料比率製品を確立できるよう支援'),
  ];

  return (
    <section className="section-padding bg-white overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-1 h-10 rounded-full bg-gradient-to-b from-brand-green to-brand-blue flex-shrink-0" />
          <div>
            <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest">
              {t('再生複合塑料', 'Recycled Composite Plastics', '再生複合プラスチック')}
            </p>
            <h2 className="font-bold text-neutral-dark" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.3' }}>
              {t('rvPlastics 再生複合塑料', 'rvPlastics Recycled Composite Plastics', 'rvPlastics 再生複合プラスチック')}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <p className="text-neutral-mid mb-8" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
              {t(
                'rvPlastics 著重於複雜來源廢棄塑膠系統，包含不同來源、不同材質的混合廢料，透過聚量技術進行相容再生，讓原本難以處理的廢棄塑膠重新回到產品生命週期中。',
                'rvPlastics focuses on complex-source waste plastic systems, including mixed waste from different sources and materials. Through PolyQ\'s compatible recycling technology, waste plastics that were previously difficult to process are returned to the product lifecycle.',
                'rvPlasticsは異なる源泉・異なる材質の混合廃材を含む複雑な廃プラスチックシステムに焦点を当てています。聚量の相容再生技術により、従来処理困難だった廃プラスチックを製品ライフサイクルに戻します。'
              )}
            </p>

            <div className="space-y-4 mb-8">
              {materialTypes.map((mt) => (
                <div key={mt.title} className="p-5 rounded-xl bg-[#F5F8FB] border border-neutral-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${mt.iconBg} flex-shrink-0`}>
                      <i className={`${mt.icon} ${mt.iconColor} text-lg`}></i>
                    </div>
                    <p className="font-semibold text-neutral-dark text-[1.0625rem]">{mt.title}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-12">
                    {mt.items.map((item) => (
                      <span key={item} className="inline-block px-2.5 py-0.5 rounded-full bg-white border border-neutral-border text-neutral-mid text-[0.9375rem]">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-green text-white text-[1.0625rem] font-semibold hover:bg-brand-green-dark transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line"></i>
              {t('詢問 rvPlastics 規格與報價', 'Inquire About rvPlastics Specifications & Pricing', 'rvPlastics仕様・価格についてお問い合わせ')}
            </Link>
          </div>

          <div>
            <div className="relative rounded-2xl overflow-hidden mb-6" style={{ height: '300px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=mixed%20recycled%20composite%20plastic%20pellets%20rPET%20rPP%20rPE%20rABS%20various%20colors%20industrial%20circular%20economy%20sustainable%20material%20clean%20white%20background%20professional%20product%20photography%20green%20blue%20technology%20eco%20material%20science%20high%20resolution&width=640&height=300&seq=rvplastics-detail-v1&orientation=landscape"
                alt={t('rvPlastics 再生複合塑料', 'rvPlastics Recycled Composite Plastics', 'rvPlastics 再生複合プラスチック')}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-green text-white text-[0.9375rem] font-semibold">
                  <i className="ri-recycle-line text-sm"></i>
                  {t('複合廢棄塑料相容再生', 'Compatible Composite Plastic Recycling', '複合廃プラスチック相容再生')}
                </span>
              </div>
            </div>

            <div className="rounded-card border border-neutral-border p-6 bg-white" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
              <p className="text-[1rem] font-semibold text-neutral-dark mb-4 uppercase tracking-wide">
                {t('rvPlastics 核心價值', 'rvPlastics Core Value', 'rvPlastics コアバリュー')}
              </p>
              <div className="space-y-3">
                {coreValues.map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-double-line text-brand-green text-base"></i>
                    </div>
                    <span className="text-neutral-dark text-[1rem] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
